/* eslint-disable @typescript-eslint/no-this-alias */
import React from 'react';
import { Form, Select, Input, DatePicker, Radio, Checkbox, Announcement, Button, message } from '@block/plug';
import { datejs, isEmpty } from '@block/utils';
import { Modal, notification } from '@ss/mtd-react';
import { SuccessFile } from '@src/types/self';
import { Address, HoverText } from '@src/components';
import { PoliticalStatusDictIndex, SignSystemUrlMap } from '@src/consts';
import { ButtonProps } from '@block/plug/lib/button/Button';
import { Upload, OcrUpload } from '../../components';
import Url from './url';
import { doAction, getEnv, getPattern } from '../../utils';
import './index.scss';
import { DictList } from './types';

const labelWidth = 120;
const SignStatusEnum = {
  NoProtocol: 'NoProtocol',
  Signed: 'Signed',
  Unsigned: 'Unsigned',
};

interface Props {
  ref: any;
  needComputerTypeValidate: boolean;
}
interface State {
  positionSimpleInfo: any;
  placeStatus: boolean; // 当国籍选择非中国时, 隐藏 民族 籍贯  户口类型 户口所在地 政治面貌
  handicappedStatus: boolean; // 如不是残障 需要隐藏残障号和残障图片
  socialSecurityStatus: boolean; // 是否展示社保
  basicInfo: any;
  showReceiveInfo: boolean;
  showReceiveProtocol: boolean;
  isRemoteEntry: boolean;
  warehouseAddress: string;
  protocolSignStatus: string;
  companyEmail?: string[];
}

class PersonInfo extends React.Component<Props, State> {
  dataChanged: boolean;

  formRef = React.createRef<Form>();

  hometownCitySelectRef = React.createRef<Select>();

  registerCitySelectRef = React.createRef<Select>();

  residentialCitySelectRef = React.createRef<Select>();

  timerId: any;

  constructor(props) {
    super(props);
    this.dataChanged = false;
    this.state = {
      positionSimpleInfo: {},
      placeStatus: false, // 当国籍选择非中国时, 隐藏 民族 籍贯  户口类型 户口所在地 政治面貌
      handicappedStatus: true, // 如不是残障 需要隐藏残障号和残障图片
      socialSecurityStatus: true, // 是否展示社保
      basicInfo: null,
      // v4.2.11
      showReceiveInfo: false, // 是否展示收件人相关信息
      showReceiveProtocol: false, // 是否展示补贴笔记本协议
      protocolSignStatus: SignStatusEnum.NoProtocol, // 补贴笔记本协议签署状态：NoProtocol：无协议；Signed：已签署；Unsigned：未签署
      isRemoteEntry: false, // 是否远程入职
      warehouseAddress: '', // s2仓地址
      companyEmail: [],
    };
    // 轮训协议签署状态的定时器
    this.timerId = null;
  }

  async componentDidMount() {
    this.getCompanyEmail();
    const { needComputerTypeValidate = false } = this.props;
    const positionSimpleInfo: DefaultAny = (await doAction(Url.positionSimpleInfo)) || {};
    const data = (await doAction(Url.getInfo, {}, null)) || {};
    // console.log('getInfo', data);
    const { nationalityDictIndex, isHandicapped, hasSocialSecurity } = data;
    let socialSecurityStatus = false;
    if (hasSocialSecurity === 0) {
      if (positionSimpleInfo?.beiJing && positionSimpleInfo.formalContract) {
        socialSecurityStatus = false;
      } else {
        socialSecurityStatus = true;
      }
    } else {
      socialSecurityStatus = true;
    }
    //  v4.2.11
    // 获取远程入职和S2仓信息
    const { isRemoteEntry, warehouseAddress } = (await doAction(Url.getEntryTypeAndComputer, {}, null)) || {};
    // 获取协议签署状态
    const protocolSignStatus = await this.getSignStatus();
    const isNeedReceiver = this.judgeNeedReceiver(isRemoteEntry, data?.computerType, warehouseAddress);
    // 当候选人在“选择办公电脑”处选择了包含“补贴笔记本”的枚举值
    const isNeedReceiveProtocol = this.judgeNeedProtocol(data?.computerType);
    this.setState({
      basicInfo: data,
      positionSimpleInfo,
      placeStatus: nationalityDictIndex !== 'CHN',
      handicappedStatus: isHandicapped === 0,
      socialSecurityStatus,
      // 是否远程入职
      isRemoteEntry,
      // 是否展示s2仓地址
      warehouseAddress,
      // 判断笔记本类型，展示协议或者收件人信息
      showReceiveProtocol: isNeedReceiveProtocol,
      showReceiveInfo: isNeedReceiver,
      protocolSignStatus,
    });
    // v4.2.11 省市区 转换为对象
    let recipientAddr = null;
    if (data?.recipientProvinceDictName && data?.recipientCityDictName && data?.recipientDistrictDictName) {
      recipientAddr = {
        province: {
          name: data?.recipientProvinceDictName,
          code: data?.recipientProvinceDictIndex,
        },
        city: {
          name: data?.recipientCityDictName,
          code: data?.recipientCityDictIndex,
        },
        district: {
          name: data?.recipientDistrictDictName,
          code: data?.recipientDistrictDictIndex,
        },
      };
    }
    this.formRef?.current?.setAllFieldsValue?.({
      ...data,
      ...{
        isHandicapped: isHandicapped === 1, // 是否残疾标识
        computerType: data?.computerType ? { code: data?.computerType, name: data?.computerType } : '',
      },
      recipientName: data?.recipientName || data?.name,
      recipientPhone: data?.recipientPhone || data?.phone,
      recipientAddr,
    });
    // 如果协议展示但未签署，轮训签署状态
    if (isNeedReceiveProtocol && protocolSignStatus === SignStatusEnum.Unsigned) {
      this.fetchSignState();
    }

    // 电脑类型在驳回后，从其他页面点击提交的时候，需要跳转到本页进行自动校验
    if (needComputerTypeValidate) {
      await this.handleRejectedComputerType();
    }
  }

  componentWillUnmount() {
    this.clearTimer();
  }

  getCompanyEmail = async () => {
    const companyEmail = await doAction(Url.email);
    this.setState({ companyEmail });
  };

  clearTimer = () => {
    clearInterval(this.timerId);
    this.timerId = null;
  };

  getPeriod = (value) => {
    const current = new Date().getFullYear();
    const date = new Date(value).getFullYear();
    return current - date >= 28;
  };

  // v4.2.11 获取协议签署状态
  getSignStatus = async () => {
    const { signStatus } = (await doAction(Url.getSignStatus, {}, null, false)) || {};
    // 空：无协议 3:已签署 其他:未签署
    if (signStatus === null) {
      return SignStatusEnum.NoProtocol;
    }
    if (signStatus === 3) {
      return SignStatusEnum.Signed;
    }
    return SignStatusEnum.Unsigned;
  };

  // 获取国籍
  handleFetchNationality = async (filter = '') => {
    const data = await doAction<DictList>(Url.getNationality, { filter }, null);
    return data.pageList;
  };

  // 获取省份
  handleFetchProvince = async (filter = '') => {
    const data = await doAction<DictList>(Url.getProvince, {
      filter,
      parentIndex: 'CHN',
    });
    return data.pageList;
  };

  // 兼容国际化
  handleFetchProvinceByDictIndex = async (filter = '', parentIndex = 'CHN') => {
    const data = await doAction<DictList>(Url.getProvince, {
      filter,
      parentIndex,
    });
    return data.pageList;
  };

  // 获取城市
  handleFetchCity = async (filter = '', fieldKeys = undefined, province = undefined) => {
    if (province) {
      const data = await doAction<DictList>(Url.getCity, {
        filter,
        parentExtIndex: 'CHN',
        parentIndex: province,
      });
      // 直辖市时，默认选中
      if (data?.pageList?.length && data.pageList.length === 1) {
        const item = data.pageList[0];
        this.formRef.current?.setFieldsValue({
          [fieldKeys[0]]: item?.dictIndex,
          [fieldKeys[1]]: item?.dictValue,
        });
      }
      return data.pageList;
    }
    return [];
  };

  // 获取城市（兼容国际化）
  handleFetchCityByDictIndex = async ({ filter = '', fieldKeys, nationDictIndex, province }) => {
    if (province) {
      const data = await doAction<DictList>(Url.getCity, {
        filter,
        parentExtIndex: nationDictIndex,
        parentIndex: province,
      });
      // 直辖市时，默认选中
      if (data?.pageList?.length && data.pageList.length === 1) {
        const item = data.pageList[0];
        this.formRef.current?.setFieldsValue({
          [fieldKeys[0]]: item?.dictIndex,
          [fieldKeys[1]]: item?.dictValue,
        });
      }
      return data.pageList;
    }
    return [];
  };

  // 获取民族
  handleFetchNationData = async (filter = '') => {
    const { nationalityDictIndex } = this.formRef.current.getFieldsValueIncludeHide();
    if (nationalityDictIndex) {
      const data = await doAction<DictList>(Url.getNation, {
        filter,
        parentIndex: nationalityDictIndex,
      });
      return data.pageList;
    }
    return [];
  };

  handleFetchNationDataByCHN = async (filter = '') => {
    const data = await doAction(Url.getNation, {
      filter,
      parentIndex: 'CHN',
    });
    return data.pageList;
  };

  // 获取证件类型
  handleFetchIDType = async (filter = '') => {
    const data = await doAction<DictList>(Url.getIDType, {
      filter,
    });
    return data.pageList;
  };

  // 获取户口类型
  handleFetchAccountType = async (filter = '') => {
    const data = await doAction<DictList>(
      Url.getRegisterType,
      {
        filter,
      },
      null,
    );
    return data.pageList;
  };

  // 获取政治面貌
  handleFetchPolitical = async (filter = '') => {
    const data = await doAction<DictList>(Url.getPolitical, {
      filter,
    });
    return data.pageList;
  };

  // 获取婚姻状况
  handleFetchWedStatus = async (filter = '') => {
    const data = await doAction<DictList>(Url.getWedStatus, {
      filter,
    });
    return data.pageList;
  };

  handleFetchAreaList = async (filter = '') => {
    const data = await doAction(Url.getAreaList, {
      filter,
    });
    return data.pageList;
  };

  handleFetchPeriod = (value) => {
    const current = new Date().getFullYear();
    const date = new Date(value).getFullYear();
    return current - date >= 28;
  };

  handleFetchRelation = async (filter = '') => {
    const data = await doAction<DictList>(Url.getRelation, { filter });
    return data.pageList;
  };

  handleSuccess = (type: string, file: SuccessFile) => {
    const { fileId, fileName, fileUrl } = file;
    const ref = this.formRef.current;
    if (type === 'front') {
      ref?.setFieldsValue({
        indentityCardFrontId: fileId,
        indentityCardFrontName: fileName,
        indentityCardFrontUrl: fileUrl,
      });
    } else {
      ref?.setFieldsValue({
        indentityCardBackId: fileId,
        indentityCardBackName: fileName,
        indentityCardBackUrl: fileUrl,
      });
    }
    fileId && ref.clearValidateStatus([type === 'front' ? 'indentityCardFrontUrl' : 'indentityCardBackUrl']);
    this.dataChanged = true;
  };

  // v4.2.11 判断是否展示收件人信息
  judgeNeedReceiver = (isBoardingOnline, computerVal, s2) => {
    if (!computerVal) {
      return false;
    }
    // mrn暂时不能通过lion配置
    // 当候选人满足 入职地点=“线上远程办理” ，且 选择电脑 不包含 “补贴笔记本” / “自带电脑” / “台式电脑”
    if (
      isBoardingOnline &&
      !computerVal?.includes('补贴笔记本') &&
      !computerVal?.includes('自带电脑') &&
      !computerVal?.includes('台式电脑')
    ) {
      return true;
    }
    // 当候选人满足 入职地点=“线上远程办理”，且 选择电脑包含“补贴笔记本”5个字 且S-2仓地址返回为“无”
    if (isBoardingOnline && computerVal?.includes('补贴笔记本') && !s2) {
      return true;
    }
    return false;
  };

  // v4.2.11 判断是否展示协议
  judgeNeedProtocol = (computerVal) => {
    if (!computerVal) {
      return false;
    }
    return computerVal?.includes('补贴笔记本');
  };

  handleFieldValueChange = (code, value) => {
    const ref = this.formRef.current;
    const { basicInfo, isRemoteEntry, warehouseAddress } = this.state;

    if (code === 'indentityCardFrontUrl') {
      // 身份证正面照
      const { fileInfo, ocrState, type }: any = {};
      const { fileId, fileName } = fileInfo || {};
      const hasEdit = false; // 生日 性别 是否可以编辑的标示
      const noOcrType = null;
      this.formRef?.current?.setFieldsValue({
        indentityCardFrontId: fileId,
        indentityCardFrontName: fileName,
      });
      if (ocrState === 'success' && !noOcrType.includes(type)) {
        const tempData: any = {};
        ref?.setFieldsValue(tempData);
        ref?.validateFields?.();
        const obj: any = {
          birthdayStatus: hasEdit,
        };
        if (tempData.birthdayStr) {
          obj.birthdayStr = tempData.birthdayStr;
        }
        this.setState(obj);
      }
    }
    if (code === 'indentityCardBackUrl') {
      // 身份证背面照
      const { fileId, fileName }: any = {};
      this.formRef?.current?.setFieldsValue({
        indentityCardBackId: fileId,
        indentityCardBackName: fileName,
      });
    }

    if (code === 'certTypeDictIndex') {
      // 证件类型
      let { birthday, birthdayStr, gender } = ref.getFieldsValueIncludeHide();
      if (value === 'NID' || value === 'OLDNID') {
        birthday = null;
        birthdayStr = null;
        gender = null;
      }
      ref?.setFieldsValue({
        certNumber: null,
        birthday,
        birthdayStr,
        gender,
        genderDictName: null,
      });
    }

    if (code === 'certNumber') {
      // 证件号码
      // 证件号码
      // eslint-disable-next-line prefer-const
      let { birthday, birthdayStr, certTypeDictIndex, gender, politicalStatusDictName } =
        ref.getFieldsValueIncludeHide();
      if (certTypeDictIndex === 'NID' && value.length === 18) {
        const year = value.substring(6, 10);
        const month = value.substring(10, 12);
        const day = value.substring(12, 14);
        const date = [year, month, day].join('-');
        birthday = new Date(date).getTime();
        birthdayStr = [year, month, day].join('-');
        gender = parseInt(value.substring(17, 1), 10) % 2 ? 'M' : 'F';
        ref.clearValidateStatus(['gender', 'birthday']);
      } else if (certTypeDictIndex === 'OLDNID' && value.length === 15) {
        const year = `19${value.substring(6, 8)}`;
        const month = value.substring(8, 10);
        const day = value.substring(10, 12);
        const date = [year, month, day].join('-');
        birthday = new Date(date).getTime();
        birthdayStr = [year, month, day].join('-');
        gender = parseInt(value.substring(14, 1), 10) % 2 ? 'M' : 'F';
        ref.clearValidateStatus(['gender', 'birthday']);
      }
      ref?.setFieldsValue({
        certNumber: value,
        birthday,
        birthdayStr,
        gender,
        genderDictName: value === 'M' ? '男' : '女',
      });
      if (this.handleFetchPeriod(birthday) && politicalStatusDictName && politicalStatusDictName.indexOf('团员') > -1) {
        ref?.setFieldsValue({ politicalStatusDictName: null, politicalStatusDictIndex: null });
      }
    }

    if (code === 'birthday') {
      // 生日
      const { politicalStatusDictName } = ref.getFieldsValueIncludeHide();
      if (this.getPeriod(value) && politicalStatusDictName && politicalStatusDictName.indexOf('团员') > -1) {
        ref?.setFieldsValue({ politicalStatusDictName: null, politicalStatusDictIndex: null });
      }
      ref?.setFieldsValue({
        birthdayStr: datejs(value).format('YYYY-MM-DD'),
      });
    }

    if (code === 'gender') {
      // 性别
      ref?.setFieldsValue({
        genderDictName: value === 'M' ? '男' : '女',
      });
    }

    if (code === 'nationalityDictIndex') {
      // 国籍
      this.setState({
        placeStatus: value !== 'CHN',
      });
    }

    if (code === 'hometownProvinceDictIndex') {
      // 籍贯 省份
      ref?.setFieldsValue({
        hometownCityDictIndex: null,
        hometownCityDictName: null,
      });
      this.hometownCitySelectRef.current?.props?.onFetchData('');
    }

    if (code === 'registerNationDictIndex') {
      // 户口所在地 国家
      ref?.setFieldsValue({
        registerProvinceDictIndex: null,
        registerProvinceDictName: null,
        registerCityDictIndex: null,
        registerCityDictName: null,
      });
    }

    if (code === 'registerProvinceDictIndex') {
      // 户口所在地 省份
      ref?.setFieldsValue({
        registerCityDictIndex: null,
        registerCityDictName: null,
      });
      this.registerCitySelectRef.current?.props?.onFetchData('');
    }

    if (code === 'residentialNationDictIndex') {
      // 现居住地 国家
      ref?.setFieldsValue({
        residentialProvinceDictIndex: null,
        residentialProvinceDictName: null,
        residentialCityDictIndex: null,
        residentialCityDictName: null,
      });
    }

    if (code === 'residentialProvinceDictIndex') {
      // 现居住地  省份
      ref?.setFieldsValue({
        residentialCityDictIndex: null,
        residentialCityDictName: null,
      });
      this.residentialCitySelectRef.current?.props?.onFetchData('');
    }

    if (code === 'isHandicapped') {
      this.setState({
        handicappedStatus: !value,
      });
      if (value === 0) {
        // 如果不是残疾人  清楚残疾号和残疾图片
        ref?.setFieldsValue({
          handicappedNumber: null,
          handicappedUrl: null,
          handicappedId: null,
          handicappedName: null,
        });
      }
    }

    if (code === 'hasSocialSecurity') {
      const { positionSimpleInfo } = this.state;
      if (value === 1) {
        this.setState({
          socialSecurityStatus: true,
        });
      } else if (positionSimpleInfo?.beiJing && positionSimpleInfo.formalContract) {
        this.setState({
          socialSecurityStatus: false,
        });
      } else {
        this.setState({
          socialSecurityStatus: true,
        });
      }
    }
    if (code === 'area') {
      if (value?.code) {
        ref?.setFieldsValue({
          area: value?.code,
        });
      }
    }
    // 收件地址
    if (code === 'recipientAddr') {
      const province = value?.province || {};
      const city = value?.city || {};
      const district = value?.district || {};
      // 户口所在地
      ref?.setFieldsValue({
        recipientProvinceDictIndex: province.code,
        recipientProvinceDictName: province.name,
        recipientCityDictIndex: city.code,
        recipientCityDictName: city.name,
        recipientDistrictDictIndex: district.code,
        recipientDistrictDictName: district.name,
      });
    }

    // v4.2.11 修改办公电脑选择
    if (code === 'computerType') {
      const isNeedReceiver = this.judgeNeedReceiver(isRemoteEntry, value, warehouseAddress);
      const isNeedReceiveProtocol = this.judgeNeedProtocol(value);
      this.setState(
        {
          showReceiveInfo: isNeedReceiver,
          showReceiveProtocol: isNeedReceiveProtocol,
        },
        () => {
          const { phone, recipientName, recipientPhone } = ref.getFieldsValueIncludeHide();
          ref?.setFieldsValue({
            recipientName: recipientName || basicInfo?.name,
            recipientPhone: recipientPhone || phone,
          });
        },
      );
      // 如果不需要展示协议，则停止已有的轮训
      if (!isNeedReceiveProtocol) {
        this.clearTimer();
      }
    }
    this.dataChanged = true;
  };

  handleHandicapped = (file) => {
    const { fileId, fileName, fileUrl } = file;
    const ref = this.formRef.current;
    ref?.setFieldsValue({
      handicappedUrl: fileUrl,
      handicappedId: fileId,
      handicappedName: fileName,
    });
    this.dataChanged = true;
    fileId && ref.clearValidateStatus(['handicappedUrl']);
  };

  handleSocialSecurityUrl = (file) => {
    const { fileId, fileName, fileUrl } = file;
    const ref = this.formRef.current;
    ref?.setFieldsValue({
      socialSecurityUrl: fileUrl,
      socialSecurityId: fileId,
      socialSecurityName: fileName,
    });
    this.dataChanged = true;
    fileId && ref.clearValidateStatus(['socialSecurityUrl']);
  };

  /** 清空电脑类型，校验，此时会飘红电脑表单项 */
  handleRejectedComputerType = async () => {
    const ref = this.formRef.current;
    // 清空电脑类型
    await ref.setFieldsValue({
      computerType: null,
    });

    // 进行一下提示
    notification.warning({
      title: '注意',
      message: '请选择办公电脑',
    });

    // 执行保存会自动校验，此时会飘红电脑表单项
    await this.handleSave();
  };

  /** 查看是否选择了错误的电脑类型 */
  getRightComputerType = async () => {
    let isRightComputerType = false;
    try {
      /** 查询办公电脑是否符合当前JF/JG */
      isRightComputerType = await doAction(Url.getComputerType, null);
    } catch (error) {
      isRightComputerType = false;
    }
    return isRightComputerType;
  };

  // 保存
  handleSave = async () => {
    const { protocolSignStatus, showReceiveProtocol, showReceiveInfo } = this.state;
    const ref = this.formRef.current;
    const valid = ref?.validateFields?.();
    // 判断协议是否签署
    if (showReceiveProtocol && protocolSignStatus !== SignStatusEnum.Signed) {
      message.error({
        message: '请先完成文件签署',
      });
      return false;
    }
    if (!valid) {
      return false;
    }
    let shouldDisableProtocol = true;
    if (protocolSignStatus !== SignStatusEnum.NoProtocol && !showReceiveProtocol) {
      shouldDisableProtocol = await new Promise((resolve) => {
        Modal.info({
          // title: '信息提示',
          message: '由于选择电脑类型变为非补贴本，故之前签署的补贴类笔记本电脑申请协议已作废',
          okBtnProps: { type: 'primary' },
          okText: '确认',
          onOk: async () => {
            await this.disableProtocol();
            resolve(true);
          },
          onCancel: () => {
            resolve(false);
          },
        });
      });
    }
    if (shouldDisableProtocol) {
      const params = this.handleFetchFinalData() || {};
      // 拿到的是 getFieldsValueIncludeHide ，所以需要删除这些收件人信息，对象引用类型，所以可以删除
      if (!showReceiveInfo) {
        [
          'recipientName',
          'recipientPhone',
          'recipientAddr',
          'recipientProvinceDictIndex',
          'recipientProvinceDictName',
          'recipientCityDictIndex',
          'recipientCityDictName',
          'recipientDistrictDictIndex',
          'recipientDistrictDictName',
          'recipientDetailAddress',
        ].forEach((key) => {
          delete params?.[key];
        });
      }
      const data = await doAction(Url.save, params);
      // 保存后再次请求电脑类型，如果电脑类型不对，返回false并且重新校验定位
      const isRightComputerType = await this.getRightComputerType();
      if (!isRightComputerType) {
        this.handleRejectedComputerType();
        return false;
      }
      return data === undefined ? false : data;
    }
    return false;
  };

  // 获取最终组织数据
  handleFetchFinalData = () => {
    const formData = this.formRef.current.getFieldsValueIncludeHide();
    const { isHandicapped } = formData;
    formData.isHandicapped = isHandicapped ? 1 : 0;
    return formData;
  };

  // 获取电脑选择
  getComputer = async (filter) => {
    const { pageList } = await doAction(Url.computerTypes, { filter: filter || '' }, null);
    return pageList;
  };

  // 查询签署状态
  fetchSignState = async () => {
    const Interval = 3000;
    try {
      // 调用后端接口获取数据
      const signStatus = await this.getSignStatus();
      this.setState({ protocolSignStatus: signStatus });
      // 未签署并且没有已有的轮训，则开始一个新的轮训
      if (signStatus === SignStatusEnum.Unsigned && !this.timerId) {
        this.timerId = setInterval(this.fetchSignState, Interval); // 每隔1秒轮询一次
      } else if (signStatus === SignStatusEnum.Signed || signStatus === SignStatusEnum.NoProtocol) {
        // 签署完成
        this.clearTimer();
      }
    } catch (error) {
      console.error('轮询出错:', error);
    }
  };

  // v4.2.11 创建协议
  createProtocol = async () => {
    // TODO :需要验证下失败状态
    const data = (await doAction(Url.createProtocol, {}, null, false)) || {};
    return data;
  };

  // v4.2.11 废弃协议
  disableProtocol = async () => {
    // TODO :需要验证下失败状态
    const data = (await doAction(Url.disableProtocol, {}, null)) || {};
    return data;
  };

  // 点击签署协议
  handleProtocolSign: ButtonProps['onClick'] = async (e, b: Button) => {
    b?.loading(true);
    try {
      const protocolSignStatus = await this.getSignStatus();
      const env = getEnv();
      if (protocolSignStatus === SignStatusEnum.Signed) {
        this.setState({
          protocolSignStatus,
        });
      } else if (protocolSignStatus === SignStatusEnum.Unsigned) {
        this.fetchSignState();
      } else if (protocolSignStatus === SignStatusEnum.NoProtocol) {
        // 生成协议
        await this.createProtocol();
        this.fetchSignState();
      }
      window.open(SignSystemUrlMap[env], '_blank');
    } finally {
      b?.loading(false);
    }
  };

  handleNationalityDictChange = (val) => {
    if (val && val?.dictValue !== '中国') {
      const ref = this?.formRef?.current;
      ref?.setFieldsValue({
        hometownProvinceDictName: null,
        hometownProvinceDictIndex: null,
        hometownCityDictName: null,
        hometownCityDictIndex: null,
      });
    }
  };

  handlePoliticalStatusChange = (val) => {
    if (val && val?.dictIndex === PoliticalStatusDictIndex.NonPartisanPersons) {
      Modal.info({
        title: '无党派人士和群众的区别',
        message: (
          <>
            <p>政治参与度：无党派人士往往会直接参与政治活动和公共事务，而群众则主要通过间接方式参与。</p>
            <p>
              社会影响力：无党派人士通常具备一定的社会影响力和专业背景，可以对政策和社会发展产生实际影响；而群众主要作为政策的接受者和反馈者。
            </p>
            <p>角色定位：无党派人士在政治体系中具有独立观察和咨询的角色，而群众是政策实施和社会管理的基础群体。</p>
          </>
        ),
        okBtnProps: { type: 'primary' },
        okText: '知道了',
      });
    }
  };

  render() {
    const self = this;
    const { ref } = this.props;
    const {
      placeStatus,
      handicappedStatus,
      socialSecurityStatus,
      showReceiveInfo,
      showReceiveProtocol,
      protocolSignStatus,
      companyEmail,
    } = this.state;
    return (
      <div className="person" ref={ref}>
        <Form
          className="person__form"
          ref={this.formRef}
          labelWidth={`${labelWidth}px`}
          onFieldValueChange={this.handleFieldValueChange}
          // useContext
        >
          <Form.Item
            formItemKey="certTypeDictIndex"
            labelInValue={false}
            formItemLabel="certTypeDictName"
            span={11}
            label="证件类型："
            required
            message="证件类型不能为空"
          >
            <Select
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              onFetchData={(filter) => this.handleFetchIDType(filter)}
              placeholder="请选择"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                getContainer: () => document.querySelector('.person__form'),
              }}
            />
          </Form.Item>
          <Form.Item span={11} />
          <Form.Item
            formItemKey="indentityCardFrontUrl"
            span={7}
            required
            message="不能为空"
            labelWidth={0}
            style={
              {
                'padding-left': `${labelWidth}px`,
              } as any
            }
          >
            <OcrUpload
              toFormItem
              fileType="11"
              url={Url.upload}
              parent={this.formRef}
              label="证件正面"
              onChange={(file: SuccessFile) => this.handleSuccess('front', file)}
            />
          </Form.Item>
          <Form.Item
            formItemKey="indentityCardBackUrl"
            span={6}
            offset={0.2}
            labelWidth={0}
            required
            message="不能为空"
          >
            <OcrUpload
              toFormItem
              fileType="12"
              url={Url.upload}
              label="证件反面"
              parent={this.formRef}
              onChange={(file: SuccessFile) => this.handleSuccess('back', file)}
            />
          </Form.Item>
          <Form.Item span={11} />

          <Form.Item
            formItemKey="certNumber"
            span={11}
            label="证件号码："
            required
            rules={[
              { required: true, message: '证件号码不能为空' },
              {
                validator(rule, value, callback) {
                  const { certTypeDictIndex } = self.formRef.current.getFieldsValueIncludeHide();
                  let str;
                  if (certTypeDictIndex === 'NID') {
                    str =
                      '^[1-9]\\d{5}(18|19|([23]\\d))\\d{2}((0[1-9])|(10|11|12))(([0-2][1-9])|10|20|30|31)\\d{3}[0-9Xx]$';
                  } else if (certTypeDictIndex === 'OLDNID') {
                    str = '^[1-9]\\d{5}\\d{2}((0[1-9])|(10|11|12))(([0-2][1-9])|10|20|30|31)\\d{3}$';
                  } else {
                    str = '^[0-9a-zA-Z]+$';
                  }
                  const pattern = getPattern(str);
                  if (pattern && !pattern.test(value)) {
                    callback('请输入有效的证件号码');
                  } else {
                    callback();
                  }
                },
              },
            ]}
          >
            <Input toFormItem placeholder="请输入" maxLength={50} />
          </Form.Item>

          <Form.Item formItemKey="birthday" span={11} offset={2} label="生日：" required message="生日不能为空">
            <DatePicker
              toFormItem
              valueFormat="timestamp"
              popLayer={{
                getContainer: () => document.querySelector('.person__form'),
              }}
            />
          </Form.Item>
          <Form.Item formItemKey="gender" span={11} label="性别：" required message="性别不能为空">
            <Radio.Group toFormItem>
              <Radio value="M">男</Radio>
              <Radio value="F">女</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            formItemKey="nationalityDictIndex"
            labelInValue={false}
            formItemLabel="nationalityDictName"
            span={11}
            offset={2}
            label="国籍："
            required
            message="国籍不能为空"
          >
            <Select
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              onFetchData={(filter) => this.handleFetchNationality(filter)}
              placeholder="请选择"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                getContainer: () => document.querySelector('.person__form'),
              }}
              onSelect={this.handleNationalityDictChange}
            />
          </Form.Item>

          <Form.Item
            formItemKey="nationDictIndex"
            labelInValue={false}
            formItemLabel="nationDictName"
            span={11}
            label="民族："
            required
            message="民族不能为空"
            hide={placeStatus}
          >
            <Select
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              // 不需要考虑其他国家，如果选择其他国家，籍贯等表单项不需要填写
              onFetchData={(filter) => this.handleFetchNationData(filter)}
              placeholder="请选择"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                getContainer: () => document.querySelector('.person__form'),
              }}
            />
          </Form.Item>
          <Form.Item
            formItemKey="hometownProvinceDictIndex"
            labelInValue={false}
            formItemLabel="hometownProvinceDictName"
            span={5}
            offset={2}
            label="籍贯："
            required
            message="籍贯不能为空"
            hide={placeStatus}
          >
            <Select
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              onFetchData={(filter) => this.handleFetchProvince(filter)}
              placeholder="请选择"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                width: 150,
                getContainer: () => document.querySelector('.person__form'),
              }}
            />
          </Form.Item>
          <Form.Item
            className="person__space"
            formItemKey="hometownCityDictIndex"
            labelInValue={false}
            formItemLabel="hometownCityDictName"
            span={6}
            labelWidth={0}
            required
            hide={placeStatus}
            message="不能为空"
          >
            <Select
              ref={this.hometownCitySelectRef}
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              onFetchData={(filter) => {
                const { hometownProvinceDictIndex } = this.formRef.current.getFieldsValueIncludeHide();
                return this.handleFetchCity(
                  filter,
                  ['hometownCityDictIndex', 'hometownCityDictName'],
                  hometownProvinceDictIndex,
                );
              }}
              placeholder="请选择"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                getContainer: () => document.querySelector('.person__form'),
              }}
            />
          </Form.Item>

          <Form.Item
            formItemKey="registerTypeDictIndex"
            labelInValue={false}
            formItemLabel="registerTypeDictName"
            span={11}
            label={
              <HoverText
                message="请参照户口本首页的“户别”信息填写，如“户别”信息中未体现是否为农业/非农业（或城镇）/居民户口，可根据户口地址进行判断，如户口地址在城市，请填写“非农业户口”，如户口地址在乡/村，请填写“农业户口”。"
                extra="："
              >
                户口类型
              </HoverText>
            }
            required
            message="户口类型不能为空"
            hide={placeStatus}
          >
            <Select
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              onFetchData={(filter) => this.handleFetchAccountType(filter)}
              placeholder="请选择"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                getContainer: () => document.querySelector('.person__form'),
              }}
            />
          </Form.Item>
          <Form.Item
            formItemKey="politicalStatusDictIndex"
            labelInValue={false}
            formItemLabel="politicalStatusDictName"
            span={11}
            offset={2}
            label="政治面貌："
            required
            message="政治面貌不能为空"
            hide={placeStatus}
          >
            <Select
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              onFetchData={(filter) => this.handleFetchPolitical(filter)}
              onSelect={this.handlePoliticalStatusChange}
              placeholder="请选择"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                getContainer: () => document.querySelector('.person__form'),
              }}
            />
          </Form.Item>

          <Form.Item
            formItemKey="registerNationDictIndex"
            labelInValue={false}
            formItemLabel="registerNationDictName"
            span={5}
            label={
              <HoverText message="请与身份证上地址保持一致，如此地址已变更请联系员工帮助台。" extra="：">
                户口所在地
              </HoverText>
            }
            required
            message="户口所在地不能为空"
            hide={placeStatus}
          >
            <Select
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              onFetchData={(filter) => this.handleFetchNationality(filter)}
              placeholder="请选择"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                width: 150,
                getContainer: () => document.querySelector('.person__form'),
              }}
            />
          </Form.Item>
          <Form.Item
            className="person__space"
            formItemKey="registerProvinceDictIndex"
            labelInValue={false}
            formItemLabel="registerProvinceDictName"
            span={3}
            labelWidth={0}
            required
            hide={placeStatus}
            message="不能为空"
          >
            <Select
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              // 兼容国际化
              onFetchData={(filter) => {
                const { registerNationDictIndex = 'CHN' } = this.formRef.current.getFieldsValueIncludeHide();
                return this.handleFetchProvinceByDictIndex(filter, registerNationDictIndex);
              }}
              placeholder="请选择"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                width: 150,
                getContainer: () => document.querySelector('.person__form'),
              }}
            />
          </Form.Item>
          <Form.Item
            className="person__space"
            formItemKey="registerCityDictIndex"
            labelInValue={false}
            formItemLabel="registerCityDictName"
            span={3}
            labelWidth={0}
            required
            hide={placeStatus}
            message="不能为空"
          >
            <Select
              ref={this.registerCitySelectRef}
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              // 兼容国际化
              onFetchData={(filter) => {
                const { registerNationDictIndex = 'CHN', registerProvinceDictIndex = '' } =
                  self.formRef.current.getFieldsValueIncludeHide();
                return this.handleFetchCityByDictIndex({
                  filter,
                  fieldKeys: ['registerCityDictIndex', 'registerCityDictName'],
                  nationDictIndex: registerNationDictIndex,
                  province: registerProvinceDictIndex,
                });
              }}
              placeholder="请选择"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                getContainer: () => document.querySelector('.person__form'),
              }}
            />
          </Form.Item>

          <Form.Item
            formItemKey="residentialNationDictIndex"
            labelInValue={false}
            formItemLabel="residentialNationDictName"
            span={5}
            offset={placeStatus ? 0 : 2}
            label="现居住地："
            required
            message="现居住地不能为空"
          >
            <Select
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              onFetchData={(filter) => this.handleFetchNationality(filter)}
              placeholder="请选择"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                width: 150,
                getContainer: () => document.querySelector('.person__form'),
              }}
            />
          </Form.Item>
          <Form.Item
            className="person__space"
            formItemKey="residentialProvinceDictIndex"
            labelInValue={false}
            formItemLabel="residentialProvinceDictName"
            span={3}
            labelWidth={0}
            required
            message="不能为空"
          >
            <Select
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              // 兼容国际化
              onFetchData={(filter) => {
                const { residentialNationDictIndex = 'CHN' } = this.formRef.current.getFieldsValueIncludeHide();
                return this.handleFetchProvinceByDictIndex(filter, residentialNationDictIndex);
              }}
              placeholder="请选择"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                width: 150,
                getContainer: () => document.querySelector('.person__form'),
              }}
            />
          </Form.Item>
          <Form.Item
            className="person__space"
            formItemKey="residentialCityDictIndex"
            labelInValue={false}
            formItemLabel="residentialCityDictName"
            span={3}
            labelWidth={0}
            required
            message="不能为空"
          >
            <Select
              ref={this.residentialCitySelectRef}
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              // 兼容国际化
              onFetchData={(filter) => {
                const { residentialNationDictIndex = 'CHN', residentialProvinceDictIndex = '' } =
                  self.formRef.current.getFieldsValueIncludeHide();
                return this.handleFetchCityByDictIndex({
                  filter,
                  fieldKeys: ['residentialCityDictIndex', 'residentialCityDictName'],
                  nationDictIndex: residentialNationDictIndex,
                  province: residentialProvinceDictIndex,
                });
              }}
              placeholder="请选择"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                getContainer: () => document.querySelector('.person__form'),
              }}
            />
          </Form.Item>
          <Form.Item span={11} hide={!placeStatus} />

          <Form.Item
            formItemKey="registerAddress"
            span={11}
            hide={placeStatus}
            labelWidth={0}
            labelPosition="top"
            style={
              {
                'padding-left': `${labelWidth + 4}px`,
              } as any
            }
            rules={[
              { required: true, message: '不能为空' },
              {
                validator(rule, value, callback) {
                  if (value && value.length > 254) {
                    callback('字数过长');
                  } else {
                    callback();
                  }
                },
              },
            ]}
          >
            {/* 允许用户输入到300，超过300字符不允许输入，并且字符超过254提示 */}
            <Input toFormItem maxLength={300} placeholder="请与身份证上地址保持一致" />
          </Form.Item>
          <Form.Item
            formItemKey="residentialDetailAddress"
            span={11}
            offset={placeStatus ? 0 : 2}
            labelWidth={0}
            labelPosition="top"
            style={
              {
                'padding-left': `${labelWidth + 4}px`,
              } as any
            }
            rules={[
              { required: true, message: '不能为空' },
              {
                validator(rule, value, callback) {
                  if (value && value.length > 254) {
                    callback('字数过长');
                  } else {
                    callback();
                  }
                },
              },
            ]}
          >
            <Input toFormItem maxLength={300} placeholder="请填写精确到门牌号的详细地址" />
          </Form.Item>
          <Form.Item span={11} hide={!placeStatus} />

          <Form.Item
            formItemKey="maritalStatusDictIndex"
            labelInValue={false}
            formItemLabel="maritalStatusDictName"
            span={11}
            label="婚姻状况："
            required
            message="婚姻状况不能为空"
          >
            <Select
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              onFetchData={(filter) => this.handleFetchWedStatus(filter)}
              placeholder="请选择"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                getContainer: () => document.querySelector('.person__form'),
              }}
            />
          </Form.Item>
          <Form.Item
            formItemKey="hasChildren"
            span={11}
            offset={2}
            label="子女状况："
            required
            message="子女状况不能为空"
          >
            <Radio.Group toFormItem>
              <Radio value={1}>有</Radio>
              <Radio value={0}>无</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item formItemKey="isHandicapped" span={11} label="残疾/残障人士：">
            <Checkbox toFormItem>残障人士</Checkbox>
          </Form.Item>
          <Form.Item span={10} />
          <Form.Item
            formItemKey="handicappedNumber"
            hide={handicappedStatus}
            required
            message="残障号不能为空"
            span={11}
            label="残障号："
          >
            <Input toFormItem placeholder="请输入残疾人证号" maxLength={45} />
          </Form.Item>
          <Form.Item hide={handicappedStatus} span={10} />
          <Form.Item
            formItemKey="handicappedUrl"
            hide={handicappedStatus}
            required
            span={11}
            labelWidth={0}
            labelPosition="top"
            message="残障证明不能为空"
            style={
              {
                'padding-left': `${labelWidth + 4}px`,
              } as any
            }
          >
            <Upload
              toFormItem
              url={Url.upload}
              label="请上传残障证明文件"
              onChange={(file) => this.handleHandicapped(file)}
            />
          </Form.Item>

          <Form.Item labelWidth={0}>
            <div className="base-form__dividing" />
          </Form.Item>

          <Form.Item labelWidth={0}>
            <span className="base-form__title">联系方式</span>
          </Form.Item>
          <Form.Item formItemKey="area" span={5} label="移动电话：" required message="不能为空">
            <Select
              toFormItem
              filterable
              keyField="code"
              labelField="name"
              renderInputLabel={(option) => `+${option.value}`}
              renderOptionContent={(option: any) => `${option.name}(+${option.code})`}
              onFetchData={(filter) => this.handleFetchAreaList(filter)}
              placeholder="请选择"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                width: 150,
                getContainer: () => document.querySelector('.person__form'),
              }}
            />
          </Form.Item>
          <Form.Item
            className="person__space"
            formItemKey="phone"
            span={6}
            labelWidth={0}
            rules={[
              { required: true, message: '移动电话不能为空' },
              {
                validator(rule, value, callback) {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { nationalityDictIndex } = self.formRef.current.getFieldsValueIncludeHide();
                  const str = '^([0-9]{3,24})$';
                  const pattern = getPattern(str);
                  if (isEmpty(value)) {
                    callback('移动电话不能为空');
                  } else if (value.length > 11) {
                    callback('字数过长');
                  } else if (pattern && !pattern.test(value)) {
                    callback('请输入有效的移动电话');
                  } else {
                    callback();
                  }
                },
              },
            ]}
          >
            <Input toFormItem maxLength={50} />
          </Form.Item>
          <Form.Item
            formItemKey="email"
            span={11}
            offset={2}
            label="个人邮箱："
            rules={[
              { required: true, message: '个人邮箱不能为空' },
              {
                validator(rule, value, callback) {
                  const str = '^([a-zA-Z0-9]+[_|.|-]?)*[a-zA-Z0-9]+@([a-zA-Z0-9]+[_.]?)*[a-zA-Z0-9]+.[a-zA-Z]{2,3}$';
                  const pattern = getPattern(str);
                  // 增加个人邮箱后缀校验，禁止公司邮箱
                  const domain = value.split('@')[1];
                  if (value && value.length > 30) {
                    callback('字数过长');
                  } else if (pattern && !pattern.test(value)) {
                    callback('个人邮箱格式不正确');
                  } else if (value && companyEmail?.includes(domain)) {
                    callback('请填写个人邮箱（不能填写公司邮箱）');
                  } else {
                    callback();
                  }
                },
              },
            ]}
          >
            <Input toFormItem maxLength={100} />
          </Form.Item>

          <Form.Item labelWidth={0}>
            <div className="base-form__dividing" />
          </Form.Item>

          <Form.Item labelWidth={0}>
            <span className="base-form__title">紧急联系人</span>
          </Form.Item>
          <Form.Item
            formItemKey="relationName"
            span={11}
            label="姓名："
            required
            message="不能为空"
            rules={[
              { required: true, message: '不能为空' },
              {
                validator(rule, value, callback) {
                  const { name } = self.formRef.current.getFieldsValueIncludeHide();
                  if (value && value.length < 2) {
                    callback('请填写紧急联系人的完整姓名');
                  } else if (value && name && value === name) {
                    callback('紧急联系人不能是本人');
                  } else {
                    callback();
                  }
                },
              },
            ]}
          >
            <Input toFormItem maxLength={50} />
          </Form.Item>
          <Form.Item
            formItemKey="relationDictIndex"
            labelInValue={false}
            formItemLabel="relationDictName"
            span={11}
            offset={2}
            label="与本人关系："
            required
            message="不能为空"
          >
            <Select
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              onFetchData={(filter) => this.handleFetchRelation(filter)}
              placeholder="请选择"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                getContainer: () => document.querySelector('.person__form'),
              }}
            />
          </Form.Item>
          <Form.Item
            label="紧急联系电话："
            formItemKey="relationPhone"
            span={11}
            rules={[
              { required: true, message: '紧急联系电话不能为空' },
              {
                validator(rule, value, callback) {
                  const formValue = self.formRef.current.getFieldsValueIncludeHide();
                  const str = '^([0-9]{3,24})$';
                  const pattern = getPattern(str);
                  if (isEmpty(value)) {
                    callback('紧急联系电话不能为空');
                  } else if (value.length > 24) {
                    callback('字数过长');
                  } else if (pattern && !pattern.test(value)) {
                    callback('请输入有效的紧急联系电话');
                  } else if (value === formValue?.phone) {
                    callback('紧急联系人电话不能与本人电话一致');
                  } else {
                    callback();
                  }
                },
              },
            ]}
          >
            <Input toFormItem maxLength={50} />
          </Form.Item>
          <Form.Item span={6} />

          <Form.Item labelWidth={0}>
            <div className="base-form__dividing" />
          </Form.Item>

          <Form.Item labelWidth={0}>
            <span className="base-form__title">社保信息</span>
          </Form.Item>
          <Form.Item
            formItemKey="hasSocialSecurity"
            span={20}
            label="是否在即将入职的工作城市办理过社保："
            required
            message="社保信息不能为空"
          >
            <Radio.Group toFormItem>
              <Radio value={1}>是</Radio>
              <Radio value={0}>否</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item formItemKey="socialSecurityUrl" hide={socialSecurityStatus} required message="社保照片不能为空">
            <Upload
              toFormItem
              url={Url.upload}
              label="请上传白底证件照"
              onChange={(file) => this.handleSocialSecurityUrl(file)}
            />
          </Form.Item>

          <Form.Item labelWidth={0}>
            <div className="base-form__dividing" />
          </Form.Item>

          <Form.Item labelWidth={0}>
            <span className="base-form__title">选择办公电脑</span>
          </Form.Item>
          <Form.Item
            formItemKey="computerType"
            labelInValue={false}
            formItemLabel="computerName"
            span={11}
            offset={2}
            label="选择电脑："
            required
            message="选择电脑不能为空"
            style={{ marginBottom: 40 }}
          >
            <Select
              toFormItem
              filterable
              keyField="code"
              labelField="name"
              onFetchData={(filter) => this.getComputer(filter)}
              placeholder="请选择"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                getContainer: () => document.querySelector('.person__form'),
              }}
            />
          </Form.Item>
          {/* TODO：样式是否需要优化 2行 */}

          <Form.Item labelWidth={0} hide={!showReceiveInfo}>
            <span className="base-form__title">填写收件信息</span>
          </Form.Item>
          <Form.Item labelWidth={0} hide={!showReceiveInfo}>
            <Announcement
              title="如果你的直属主管在你入职前指定了电脑代收人，那么在你正式入职后，办公电脑将由该代收人交给你。如果没有指定代收人，办公电脑将会邮寄到你提供的收件地址。因此，请务必确保填写的手机号准确，且收件地址精确到“楼宇门牌号”。"
              type="error"
            />
          </Form.Item>
          <Form.Item
            formItemKey="recipientName"
            span={6}
            label="收件人："
            required
            message="收件人不能为空"
            hide={!showReceiveInfo}
            style={{ marginBottom: 40 }}
          >
            <Input toFormItem placeholder="请输入" maxLength={10} />
          </Form.Item>
          <Form.Item
            formItemKey="recipientPhone"
            span={6}
            label="手机号："
            hide={!showReceiveInfo}
            rules={[
              { required: true, message: '手机号不能为空' },
              {
                validator(rule, value, callback) {
                  const str = '^([0-9]{3,24})$';
                  const pattern = getPattern(str);
                  if (value.length !== 11 || (pattern && !pattern.test(value))) {
                    callback('请输入有效的手机号');
                  } else if (isEmpty(value)) {
                    callback('手机号不能为空');
                  } else {
                    callback();
                  }
                },
              },
            ]}
            style={{ marginBottom: 40 }}
          >
            <Input toFormItem placeholder="请输入" maxLength={11} />
          </Form.Item>
          <Form.Item
            formItemKey="recipientAddr"
            span={6}
            label="收件地址："
            required
            message="收件地址不能为空"
            style={{ marginBottom: 40 }}
            hide={!showReceiveInfo}
          >
            <Address toFormItem placeholder="请选择收件地址" />
          </Form.Item>
          <Form.Item
            formItemKey="recipientDetailAddress"
            span={6}
            labelWidth={0}
            required
            message="详细地址不能为空"
            style={{ marginBottom: 40 }}
            hide={!showReceiveInfo}
          >
            <Input toFormItem placeholder="请选择" maxLength={100} />
          </Form.Item>

          {showReceiveProtocol && (
            <>
              <Form.Item labelWidth={0}>
                <span className="base-form__title">补贴类笔记本电脑申请协议签署</span>
              </Form.Item>
              <Form.Item labelWidth={0}>
                <div className="person__form__protocol">
                  <div>
                    由于你选择了“补贴类笔记本电脑”，请及时完成《补贴类笔记本电脑申请协议》的签署。
                    <br />
                    “补贴类笔记本电脑”即由公司支付采购时的总金额（含13%税费，税费由公司承担），公司按实际成本给与员工50%的补贴，于约定的补贴期限中逐期进行折抵。员工承担“补贴类笔记本电脑”不含税单价的50%，并由公司在员工税后的月度薪资中逐期扣除。公司每年会从主流电脑厂商处公开招标（如：联想/戴尔/惠普），“补贴类笔记本电脑”的单价会根据实际发放时的市场价略有浮动，预计承担费用为70-90元/月。
                  </div>
                  <Button onClick={this.handleProtocolSign}>
                    {protocolSignStatus === SignStatusEnum.Signed ? '查看签署文件' : '开始签署'}
                  </Button>
                </div>
              </Form.Item>
            </>
          )}
        </Form>
      </div>
    );
  }
}

export default PersonInfo;
